import Auth0 from 'auth0-js'
import { PasskeyLoginMode, PasskeyPurpose, Tenant } from '@/types'
import { parseRedirectUri } from './parse-redirect-uri'
import { checkSession } from './check-session'

/**
  Redirects the user to the provided uri assuming that the uri is within the
  Auth0 tenant's authorised callbacks

  On error, the user will be redirected to an Auth0 error page
* */

interface RedirectToUniversalLoginInputs {
  tenant: Tenant
  redirectUri: string
  prompt?: string
  temporaryMfaSetup?: boolean
  purpose?: string
  email?: string
  loginHelpUrl?: string
  logoUri?: string
  state?: string
  ui_locales?: string
  // where Universal Login's "back to login" link returns to; defaults to the authx /login page
  backToLoginLink?: string
}

export const redirectToUniversalLogin = async (param: RedirectToUniversalLoginInputs) => {

  if (!param.tenant) {
    throw new Error('TenantRequired')
  }

  const webAuth = new Auth0.WebAuth({
    domain: param.tenant.loginDomain,
    clientID: param.tenant.loginClientId,
  })

  if (param.prompt === 'none') {
    await checkSession(param.tenant)
  }

  const backToLoginLink = param.backToLoginLink || `${window.location.protocol}//${window.location.host}/login`
  const { logoUri } = param

  if (param.purpose === PasskeyPurpose.passkeyEnrollment) {

    const authUrl = new URL(webAuth.client.buildAuthorizeUrl({
      clientID: param.tenant.loginClientId,
      scope: 'openid profile email',
      responseType: 'code',
      state: new Date().getTime().toString(),
      redirectUri: param.redirectUri
        ? parseRedirectUri(param.redirectUri, param.tenant.aliasCode || param.tenant.code, undefined, param.temporaryMfaSetup, param.purpose)
        : undefined,
    }))

    authUrl.searchParams.append('ext-back-to-login-link', backToLoginLink)
    authUrl.searchParams.append('prompt', 'login')
    authUrl.searchParams.append('ext-logo-uri', logoUri)
    authUrl.searchParams.append('ext-mode', PasskeyLoginMode.Enrolment)
    authUrl.searchParams.append('ext-username', param.email)
    authUrl.searchParams.append('ui_locales', param.ui_locales || 'en')

    window.location.href = authUrl.toString()

  } else if (param.purpose === PasskeyPurpose.passkeyStepUpAuthentication) {

    const authUrl = new URL(webAuth.client.buildAuthorizeUrl({
      clientID: param.tenant.loginClientId,
      scope: 'openid profile email',
      responseType: 'code',
      state: param.state,
      redirectUri: param.redirectUri
        ? parseRedirectUri(
          param.redirectUri,
          param.tenant.aliasCode || param.tenant.code,
          undefined,
          param.temporaryMfaSetup,
          PasskeyPurpose.passkeyStepUpSuccess,
        )
        : undefined,
    }))

    authUrl.searchParams.append('ext-back-to-login-link', backToLoginLink)
    authUrl.searchParams.append('prompt', 'login')
    authUrl.searchParams.append('ext-logo-uri', logoUri)
    authUrl.searchParams.append('ext-mode', PasskeyLoginMode.StepUp)
    authUrl.searchParams.append('ui_locales', param.ui_locales || 'en')

    window.location.href = authUrl.toString()

  } else {

    const loginIdPasskeysRetryLogo = `${window.location.protocol}//${window.location.host}/static/images/passkeys/retry-passkeys-box.svg`

    const authUrl = new URL(webAuth.client.buildAuthorizeUrl({
      clientID: param.tenant.loginClientId,
      scope: 'openid profile email',
      responseType: 'code',
      state: new Date().getTime().toString(),
      redirectUri: param.redirectUri
        ? parseRedirectUri(param.redirectUri, param.tenant.aliasCode || param.tenant.code, undefined, param.temporaryMfaSetup, param.purpose)
        : undefined,
    }))

    // `prompt=login` forces an interactive re-authentication — a silent SSO
    // would re-issue a still-stale session. (The passkeyEnrollment/stepUp
    // branches above already force this unconditionally.)
    if (param.prompt === 'login') {
      authUrl.searchParams.append('prompt', 'login')
    }

    authUrl.searchParams.append('ext-back-to-login-link', backToLoginLink)
    authUrl.searchParams.append('ext-logo-uri', loginIdPasskeysRetryLogo)
    authUrl.searchParams.append('ext-mode', PasskeyLoginMode.Standard)
    authUrl.searchParams.append('ui_locales', param.ui_locales || 'en')

    window.location.href = authUrl.toString()

  }

}
